<template>
  <a-modal
    :open="open"
    title="Firmas de correo"
    width="800px"
    centered
    wrapClassName="modal-divider signature-email-modal"
    @cancel="handleCancel"
    :mask-closable="false"
  >
    <div class="email-signatures">
      <p class="description">
        Crea y gestiona tus firmas de correo. Agrega una firma que aparecerá al
        final de cada correo que envíes. Puedes tener hasta 5 firmas y elegir
        cuál usar según el tipo de correo.
      </p>

      <div class="signatures-body">
        <div class="signatures-header">
          <div class="signatures-header__title">
            <strong>Mis firmas</strong>
            <span class="counter">{{ signatureCounter }}</span>
          </div>
        </div>
  
        <a-button type="dashed" class="create-button" @click="handleCreateSignature" v-if="!isLimitCreateSignature">
          <PlusOutlined />
          Crear firma
        </a-button>
  
        <a-spin :spinning="loadingData">
          <div class="signatures-panel">
            <a-empty
              v-if="!previewHasSignatures"
              class="empty-state"
              description="Aún no tienes firmas creadas. Crea una para personalizar tus correos."
              :image="simpleImage"
            >
            </a-empty>
    
            <div v-else class="signature-list">
              <div
                v-for="signature in signatures"
                :key="signature.id"
                class="signature-item"
              >
                <div class="signature-item__content">
                  <div class="signature-item__name">
                      <a-tooltip
                        :title="signature.name"
                        :arrow="false"
                        overlayClassName="custom-tooltip"
                        placement="bottomLeft"
                      >
                      <span class="signature-item__name-text">{{ signature.name }}</span>
                    </a-tooltip>
                  </div>
                  <a-tag v-if="signature.id === formState.newMessageSignature" color="magenta">Mensajes nuevos</a-tag>
                  <a-tag v-if="signature.id === formState.replySignature" color="blue">Respuestas y reenvíos</a-tag>
                </div>
                <div class="signature-item__actions">
                  <a-tooltip title="Editar firma">
                    <a-button 
                      type="link" 
                      class="signature-action" 
                      @click="handleEdit(signature)"
                    >
                      <EditOutlined style="font-size: 16px;"/>
                    </a-button>
                  </a-tooltip>
    
                  <a-popover
                    trigger="click"
                    placement="bottomRight"
                    :arrow="false"
                    overlayClassName="template-category-popover"
                  >
                    <template #content>
                      <div class="signature-assign-content">
                        <div class="signature-assign-title">
                          Asignar como predeterminada
                        </div>
    
                        <a-tooltip placement="left">
                          <template #title v-if="formState.newMessageSignature">
                            Al establecer esta firma como la nueva predeterminada, la actual será reemplazada.
                          </template>
                          <div 
                            class="signature-assign-div"
                            :class="{ 'signature-assign-div--selected': formState.newMessageSignature === signature.id}"
                            @click.stop="handleAssignNewMessage(signature.id)"
                          >
                            <a-checkbox
                              class="signature-assign-checkbox"
                              :checked="formState.newMessageSignature === signature.id"  
                            >
                              Para mensajes nuevos
                            </a-checkbox>
                          </div>
                        </a-tooltip>
    
                        <a-tooltip placement="left">
                          <template #title v-if="formState.replySignature">
                            Al establecer esta firma como la nueva predeterminada, la actual será reemplazada.
                          </template>
                          <div 
                            class="signature-assign-div"
                            :class="{ 'signature-assign-div--selected': formState.replySignature === signature.id}"
                            @click.stop="handleAssignReplyForward(signature.id)"
                          >
                            <a-checkbox
                              class="signature-assign-checkbox"
                              :checked="formState.replySignature === signature.id"  
                            >
                              Para respuestas y reenvíos
                            </a-checkbox>
                          </div>
                        </a-tooltip>
                      </div>
                    </template>
    
                    <a-tooltip title="Asignar como predeterminada">
                      <a-button
                        type="link"
                        class="signature-action"
                      >
                        <IconAssigned style="width: 16px;" />
                      </a-button>
                    </a-tooltip>
                  </a-popover>
    
                  <a-tooltip title="Eliminar firma">
                    <a-button
                      type="link"
                      class="signature-action"
                      @click="handleDelete(signature)"
                    >
                      <DeleteOutlined style="font-size: 16px;"/>
                    </a-button>
                  </a-tooltip>
                </div>
              </div>
            </div>
          </div>
        </a-spin>
      </div>

      <a-divider style="margin: 4px 0"; color="#00000026"></a-divider>

      <div class="signature-config">
        <h3>Configuración de firmas</h3>

        <p>
          Asigna una firma predeterminada según el tipo de correo.
        </p>

        <div class="select-row">
          <div class="select-group">
            <label>
              <span class="required">*</span>
              Para mensaje nuevos
              <a-tooltip title="Elige qué firma aparecerá cuando inicies un correo desde cero.">
                <InfoCircleOutlined />
              </a-tooltip>
            </label>

            <a-select
              v-model:value="formState.newMessageSignature"
              placeholder="Sin firma"
              class="signature-select"
              :options="signatureOptions"
              :disabled="!previewHasSignatures"
            />
          </div>

          <div class="select-group">
            <label>
              <span class="required">*</span>
              Para respuestas y reenvíos
              <a-tooltip title="Elige qué firma aparecerá cuando respondas o reenvíes un correo.">
                <InfoCircleOutlined />
              </a-tooltip>
            </label>

            <a-select
              v-model:value="formState.replySignature"
              placeholder="Sin firma"
              class="signature-select"
              :options="signatureOptions"
              :disabled="!previewHasSignatures"
            />
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <a-button key="back" @click="handleCancel" :disabled="isSaveDisabled">Cancelar</a-button>
      <a-button
        key="submit"
        type="primary"
        :loading="loading"
        @click="handleOk"
        :disabled="isSaveDisabled"
      >
        Guardar
      </a-button>
    </template>
  </a-modal>

  <SignatureCreateModal 
    v-model:open="createSignatureModalOpen" 
    :mode="modalMode"
    :signature="selectedSignature"
    :newMessageSignatureName="newMessageSignatureName"
    :replySignatureName="replySignatureName"
    @saved="fetchSignaturesList"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch, createVNode } from "vue";
import { cloneDeep, isEqual } from "lodash";
import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons-vue";
import IconAssigned from "@/app/common/components/atoms/IconAssigned.vue";
import SignatureCreateModal from "./SignatureCreateModal.vue";
import { emailService } from "../../services/Email.service.ts";
import { Signature } from "@/@types/emails";
import { message, Modal, Empty } from "ant-design-vue";
import { ResponseError } from "@/app/shared/utils/httpManager";

const MAX_NUMBER_SIGNATURE = 5
const simpleImage = Empty.PRESENTED_IMAGE_SIMPLE;

const props = defineProps<{
  open: boolean;
}>();

const emit = defineEmits<{
  "update:open": [value: boolean];
}>();

const loading = ref(false);
const loadingData = ref(false);

const signatures = ref<Signature[]>([]);
const modalMode = ref<string>("create");
const selectedSignature = ref<Signature | null>(null);

const formState = ref<{
  newMessageSignature: number | null;
  replySignature: number | null;
}>({
  newMessageSignature: null,
  replySignature: null,
});

const formStateCompare = ref(cloneDeep(formState.value));

const createSignatureModalOpen = ref(false);

const fetchSignaturesList = async () => {
  loadingData.value = true;
  try {
    signatures.value = await emailService.getSignatureList();
    formState.value.newMessageSignature =
      signatures.value.find((signature) => signature.is_new_message)?.id ?? null;
    formState.value.replySignature =
      signatures.value.find((signature) => signature.is_reply_forward)?.id ?? null;
    formStateCompare.value = cloneDeep(formState.value);
  } catch (error) {
    console.error("Error al listar firmas de correo", error);
    signatures.value = [];
    formState.value.newMessageSignature = null;
    formState.value.replySignature = null;
    formStateCompare.value = cloneDeep(formState.value);
  } finally {
    loadingData.value = false;
  }
};

const previewHasSignatures = computed(() => signatures.value.length > 0);

const signatureCounter = computed(() => `${signatures.value.length} / 5`);

const isLimitCreateSignature = computed(() => {
  return signatures.value.length >= MAX_NUMBER_SIGNATURE;
});


const signatureOptions = computed(() => [
  {
    label: "Sin firma",
    value: null,
  },
  ...signatures.value.map((signature) => ({
    label: signature.name,
    value: signature.id,
  })),
]);

const hasChanges = computed(() => {
  return !isEqual(formState.value, formStateCompare.value);
});

const isSaveDisabled = computed(() => {
  return !previewHasSignatures.value || !hasChanges.value;
});

const newMessageSignatureName = computed(() => {
  return signatures.value.find(
    (signature) => signature.id === formState.value.newMessageSignature
  )?.name ?? null;
});

const replySignatureName = computed(() => {
  return signatures.value.find(
    (signature) => signature.id === formState.value.replySignature
  )?.name ?? null;
});

const handleAssignNewMessage = (signatureId: number) => {
  formState.value.newMessageSignature =
    formState.value.newMessageSignature === signatureId ? null : signatureId;
};

const handleAssignReplyForward = (signatureId: number) => {
  formState.value.replySignature =
    formState.value.replySignature === signatureId ? null : signatureId;
};

const handleCreateSignature = () => {
  modalMode.value = "create";
  selectedSignature.value = null;
  createSignatureModalOpen.value = true;
};

const handleEdit = (signature: Signature) => {
  modalMode.value = "edit";
  selectedSignature.value = signature;
  createSignatureModalOpen.value = true;
};

const handleDelete = (signature: Signature) => {
  const isDefaultSignature =
    signature.id === formState.value.newMessageSignature ||
    signature.id === formState.value.replySignature;

  const content = isDefaultSignature
    ? createVNode("div", {}, [
        createVNode(
          "p",
          { class: "text-base font-medium mb-2" },
          "Al eliminar su firma ocurrirá lo siguiente:"
        ),
        createVNode(
          "ul",
          {
            class: "list-disc text-sm text-gray-600",
            style: "padding-left: 24px;",
          },
          [
            createVNode("li", {}, [
              "La firma dejará de estar disponible en el listado de Mis firmas. ",
              createVNode("strong", {}, "Esta acción no se podrá deshacer."),
            ]),
            createVNode("li", {}, [      
              'La firma dejará de estar como predeterminada en todo tipo de correo y será reemplazada por la opción "Sin firma".'
            ]),
          ]
        ),
      ])
    : createVNode("div", {}, [
        createVNode(
          "p",
          { class: "text-base font-medium mb-2" }, 
          [
            "Al eliminar la firma, esta dejará de estar disponible en el listado de Mis firmas. ",
            createVNode("strong", {}, "Esta acción no se podrá deshacer."),
          ]
        ),
      ]);

  Modal.confirm({
    centered: true,
    icon: createVNode(ExclamationCircleOutlined),
    title: `¿Estás seguro de eliminar la firma ${signature.name}?`,
    content,
    okText: "Sí, eliminar",
    cancelText: "No, cancelar",
    okButtonProps: {
      danger: true,
    },
    onOk: async () => {
      try {
        await emailService.deleteSignature(signature.id);
        message.success("Firma eliminada con éxito.");
        await fetchSignaturesList();
      } catch (error) {
        let errorMessage = "Ha ocurrido un error";
        if (error instanceof ResponseError) {
          errorMessage = error.getErrorMessage();
        }
        message.error(errorMessage);
      }
    },
  });
};

const closeCreateSignatureModal = () => {
  createSignatureModalOpen.value = false;
};

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) {
      createSignatureModalOpen.value = false;
      return;
    }

    fetchSignaturesList();
  },
  { immediate: true }
);

const handleOk = async () => {
  if (isSaveDisabled.value) return;

  loading.value = true;

  try {
    const syncExclusiveSignatureFlags = async (
      field: "is_new_message" | "is_reply_forward",
      selectedSignatureId: number | null
    ) => {
      const requests = signatures.value.flatMap((signature) => {
        const shouldBeActive = selectedSignatureId === signature.id;
        const payload: Partial<Signature> = {};

        if (signature[field] !== shouldBeActive) {
          payload[field] = shouldBeActive;
        }

        return Object.keys(payload).length
          ? [emailService.updateSignature(signature.id, payload)]
          : [];
      });

      await Promise.all(requests);
    };

    await syncExclusiveSignatureFlags("is_new_message", formState.value.newMessageSignature);
    await syncExclusiveSignatureFlags("is_reply_forward", formState.value.replySignature);

    message.success("Cambios guardados con éxito.");
    handleCancel();
  } catch (error) {
    let errorMessage = "Ha ocurrido un error";
    if (error instanceof ResponseError) errorMessage = error.getErrorMessage();
    message.error(errorMessage);
  } finally {
    loading.value = false;
  }
};

const handleCancel = () => {
  closeCreateSignatureModal();
  emit("update:open", false);
};
</script>

<style lang="sass" scoped>
.email-signatures
  display: flex
  flex-direction: column
  gap: 16px

.description
  margin-bottom: 0

.signatures-body 
  display: flex
  flex-direction: column
  gap: 12px

.signatures-header
  display: flex
  align-items: center
  justify-content: space-between

.signatures-header__title
  display: flex
  align-items: baseline
  gap: 4px

.signatures-header strong
  font-weight: 600

.counter
  color: #00000073
  font-size: 12px
  font-weight: 400

.create-button
  height: 46px
  color: $primary
  border-color: $primary

//.signatures-panel
  //min-height: 264px
  // border: none
  // border-radius: 0
  // background: transparent
  // overflow: visible

.signature-list
  display: flex
  flex-direction: column
  width: 100%
  min-height: 208px
  gap: 8px

.signature-item
  padding: 12px
  display: flex
  align-items: center
  justify-content: space-between
  gap: 16px
  border: 1px solid #00000026
  border-radius: 6px
  background: #00000005
  box-shadow: none
  overflow: hidden
  transition: border-color 0.15s ease, box-shadow 0.15s ease

  &:hover
    border-color: #bfbfbf
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03)

.signature-item__content
  min-width: 0
  //flex: 1
  display: flex
  align-items: center
  gap: 8px
  flex-wrap: nowrap

.signature-item__name
  min-width: 0
  flex: 1 1 auto
  overflow: hidden

.signature-item__name-text
  display: block
  width: 100%
  min-width: 0
  overflow: hidden
  white-space: nowrap
  text-overflow: ellipsis

.signature-item__content :deep(.ant-tag)
  flex-shrink: 0
  margin-inline-end: 0

.signature-item__actions
  display: flex
  align-items: center
  gap: 12px
  flex-shrink: 0

.signature-action
  width: 20px
  height: 20px
  display: inline-flex
  align-items: center
  justify-content: center
  padding: 0

.empty-state
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  width: 100%
  height: 208px
  box-sizing: border-box
  border: 1px solid #0000000F
  border-radius: 8px
  margin-inline: 0
  background: #00000005
  margin-block: 0

  :deep(.ant-empty-image)
    margin-bottom: 16px

  :deep(.ant-empty-description)
    color: #00000073

.signature-config
  display: flex
  flex-direction: column
  gap: 8px

.signature-config h3
  margin: 0
  font-size: 14px
  font-weight: 600

.signature-config p
  margin-bottom: 8px

.select-row
  display: grid
  grid-template-columns: repeat(2, 280px)
  gap: 24px
  align-items: start
  justify-content: start

.select-group label
  margin-bottom: 2px
  display: flex
  align-items: center
  gap: 4px

.required
  color: #FF7875
  font-size: 14px

.signature-select
  width: 100%

@media (max-width: 640px)
  .select-row
    grid-template-columns: 1fr

// popover
.signature-assign-content
  display: flex
  flex-direction: column
  width: 240px
  
.signature-assign-title
  color: #00000073
  font-size: 12px
  padding: 6px 12px

.signature-assign-div
  line-height: 32px
  display: flex
  align-items: center
  border-radius: 4px
  cursor: pointer
  transition: background-color 0.2s ease
  
  &:hover
    background: #0000000F

  &.signature-assign-div--selected
    background: #E6F4FF

    &:hover
      background: #E6F4FF

.signature-assign-checkbox
  width: 100%
  padding: 5px 12px
  margin: 0
  pointer-events: none
  
.signature-assign-checkbox
  padding: 5px 12px

:global(.template-category-popover .ant-popover-inner)
  padding: 4px

</style>

<style>
.signature-email-modal .ant-modal-body {
  padding: 20px 24px !important;
}
</style>

